import type { Lang } from './astro.ts'
import { loc, type Loc } from './cards.ts'

/**
 * Совместимость по традиционной астрологии.
 *
 * Важно понимать, что это: не измерение и не предсказание, а воспроизводимый
 * подсчёт по классическим правилам — стихии, кресты (модальности), полярность
 * и угол между знаками. Числа получаются одинаковыми для одной и той же пары
 * знаков всегда; веса ниже — это принятая в традиции значимость каждого
 * фактора, а не результат какого-либо исследования.
 *
 * Интерфейс обязан подавать это как трактовку традиции, а не как факт.
 */

export type ElementId = 'fire' | 'earth' | 'air' | 'water'
export type ModalityId = 'cardinal' | 'fixed' | 'mutable'

export const ELEMENTS: ({ id: ElementId; sym: string } & Loc)[] = [
  { id: 'fire',  ru: 'Огонь',  az: 'Od',     en: 'Fire',  tr: 'Ateş',   sym: '△' },
  { id: 'earth', ru: 'Земля',  az: 'Torpaq', en: 'Earth', tr: 'Toprak', sym: '▽' },
  { id: 'air',   ru: 'Воздух', az: 'Hava',   en: 'Air',   tr: 'Hava',   sym: '◇' },
  { id: 'water', ru: 'Вода',   az: 'Su',     en: 'Water', tr: 'Su',     sym: '◈' },
]

export const MODALITIES: ({ id: ModalityId } & Loc)[] = [
  { id: 'cardinal', ru: 'Кардинальный',  az: 'Kardinal', en: 'Cardinal', tr: 'Öncü' },
  { id: 'fixed',    ru: 'Фиксированный', az: 'Sabit',    en: 'Fixed',    tr: 'Sabit' },
  { id: 'mutable',  ru: 'Мутабельный',   az: 'Dəyişkən', en: 'Mutable',  tr: 'Değişken' },
]

/** Овен — огонь, Телец — земля, Близнецы — воздух, Рак — вода, дальше по кругу. */
export const elementOf = (sign: number): ElementId => ELEMENTS[sign % 4].id
/** Овен — кардинальный, Телец — фиксированный, Близнецы — мутабельный. */
export const modalityOf = (sign: number): ModalityId => MODALITIES[sign % 3].id
/** Огонь и воздух — активная полярность, земля и вода — восприимчивая. */
export const polarityOf = (sign: number): 'yang' | 'yin' => (sign % 2 === 0 ? 'yang' : 'yin')

/** Угол между знаками в знаках: 0 — соединение, 6 — оппозиция. */
export function aspectBetween(a: number, b: number): number {
  const d = Math.abs(a - b) % 12
  return Math.min(d, 12 - d)
}

const ASPECTS: ({ steps: number; score: number } & Loc)[] = [
  { steps: 0, score: 75, ru: 'Соединение',   az: 'Qovuşma',      en: 'Conjunction', tr: 'Kavuşum' },
  { steps: 1, score: 45, ru: 'Полусекстиль', az: 'Yarımsekstil', en: 'Semi-sextile', tr: 'Yarım sekstil' },
  { steps: 2, score: 85, ru: 'Секстиль',     az: 'Sekstil',      en: 'Sextile',     tr: 'Sekstil' },
  { steps: 3, score: 40, ru: 'Квадрат',      az: 'Kvadrat',      en: 'Square',      tr: 'Kare' },
  { steps: 4, score: 95, ru: 'Тригон',       az: 'Trigon',       en: 'Trine',       tr: 'Üçgen' },
  { steps: 5, score: 35, ru: 'Квиконс',      az: 'Kvinkonks',    en: 'Quincunx',    tr: 'Kinkunks' },
  { steps: 6, score: 70, ru: 'Оппозиция',    az: 'Oppozisiya',   en: 'Opposition',  tr: 'Karşıt' },
]

/** Сочетаемость стихий: своя — созвучна, огонь-воздух и земля-вода — питают друг друга. */
function elementScore(a: ElementId, b: ElementId): number {
  if (a === b) return 90
  const pair = [a, b].sort().join('-')
  switch (pair) {
    case 'air-fire':
    case 'earth-water':
      return 85
    case 'earth-fire':
      return 50
    case 'air-water':
      return 50
    case 'fire-water':
      return 40
    case 'air-earth':
      return 45
    default:
      return 50
  }
}

export interface CompatAxis extends Loc {
  id: 'aspect' | 'element' | 'modality' | 'polarity'
  value: number
  /** Пояснение, что именно совпало, — на всех языках. */
  detail: Loc
}

export interface CompatResult {
  signA: number
  signB: number
  /** Итоговая гармония, 0–100. */
  score: number
  axes: CompatAxis[]
  aspectSteps: number
}

const name = (list: Loc[], i: number, lang: Lang) => loc(list[i], lang)

/** Собирает пояснение оси на всех языках сразу. */
const everyLang = (build: (lang: Lang) => string): Loc => ({
  ru: build('ru'), az: build('az'), en: build('en'), tr: build('tr'),
})

/** «Обе — вода» / «Both are water»: связка отличается по языкам. */
const AND: Record<Lang, string> = { ru: 'и', az: 'və', en: 'and', tr: 've' }
const BOTH: Record<Lang, string> = { ru: 'Обе —', az: 'Hər ikisi —', en: 'Both are', tr: 'İkisi de' }
const BOTH_M: Record<Lang, string> = { ru: 'Оба', az: 'Hər ikisi', en: 'Both are', tr: 'İkisi de' }
const SAME_NATURE: Record<Lang, string> = {
  ru: 'Одна природа', az: 'Eyni təbiət', en: 'The same nature', tr: 'Aynı doğa',
}
const COMPLEMENT: Record<Lang, string> = {
  ru: 'Дополняют друг друга', az: 'Bir-birini tamamlayır',
  en: 'They complement each other', tr: 'Birbirini tamamlar',
}

export function compatibility(signA: number, signB: number): CompatResult {
  const steps = aspectBetween(signA, signB)
  const aspect = ASPECTS[steps]

  const elA = elementOf(signA)
  const elB = elementOf(signB)
  const element = elementScore(elA, elB)

  const modA = modalityOf(signA)
  const modB = modalityOf(signB)
  // Одинаковый крест — похожий способ действовать, но и соперничество:
  // такие знаки стоят друг к другу в квадрате или оппозиции.
  const modality = modA === modB ? 55 : 80

  const polarity = polarityOf(signA) === polarityOf(signB) ? 80 : 70

  const elIdxA = ELEMENTS.findIndex((e) => e.id === elA)
  const elIdxB = ELEMENTS.findIndex((e) => e.id === elB)
  const modIdxA = MODALITIES.findIndex((m) => m.id === modA)
  const modIdxB = MODALITIES.findIndex((m) => m.id === modB)

  const axes: CompatAxis[] = [
    {
      id: 'aspect', ru: 'Угол', az: 'Bucaq', en: 'Angle', tr: 'Açı', value: aspect.score,
      detail: { ru: aspect.ru, az: aspect.az, en: aspect.en, tr: aspect.tr },
    },
    {
      id: 'element', ru: 'Стихии', az: 'Ünsürlər', en: 'Elements', tr: 'Unsurlar', value: element,
      detail: everyLang((l) =>
        elA === elB
          ? `${BOTH[l]} ${name(ELEMENTS, elIdxA, l).toLowerCase()}`
          : `${name(ELEMENTS, elIdxA, l)} ${AND[l]} ${name(ELEMENTS, elIdxB, l).toLowerCase()}`,
      ),
    },
    {
      id: 'modality', ru: 'Кресты', az: 'Xaçlar', en: 'Modalities', tr: 'Nitelikler', value: modality,
      detail: everyLang((l) =>
        modA === modB
          ? `${BOTH_M[l]} ${name(MODALITIES, modIdxA, l).toLowerCase()}`
          : `${name(MODALITIES, modIdxA, l)} ${AND[l]} ${name(MODALITIES, modIdxB, l).toLowerCase()}`,
      ),
    },
    {
      id: 'polarity', ru: 'Полярность', az: 'Qütblük', en: 'Polarity', tr: 'Kutupluluk', value: polarity,
      detail: polarityOf(signA) === polarityOf(signB) ? SAME_NATURE : COMPLEMENT,
    },
  ]

  // Веса традиции: угол весит больше всего, полярность — меньше всего.
  const score = Math.round(
    aspect.score * 0.4 + element * 0.3 + modality * 0.2 + polarity * 0.1,
  )

  return { signA, signB, score, axes, aspectSteps: steps }
}

export function aspectName(steps: number, lang: Lang): string {
  return lang === 'ru' ? ASPECTS[steps].ru : ASPECTS[steps].az
}

export function elementName(id: ElementId, lang: Lang): string {
  const e = ELEMENTS.find((x) => x.id === id)!
  return loc(e, lang)
}

export function axisLabel(axis: CompatAxis, lang: Lang): string {
  return loc(axis, lang)
}

export function axisDetail(axis: CompatAxis, lang: Lang): string {
  return loc(axis.detail, lang)
}
